/**
 * Hariom ERP - Production Management
 * Track production orders, output vs planned. Orders are created/edited by
 * employees, persisted, and stats are computed live.
 */
import React, { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useProductionStore, type ProdOrder } from '../../store/productionStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'

const STATUS_MAP: Record<string, { label: string; cls: string; bg: string }> = {
  completed:   { label: 'Completed',   cls: 'badge-success', bg: 'bg-green-50' },
  in_progress: { label: 'In Progress', cls: 'badge-info',    bg: 'bg-blue-50'  },
  planned:     { label: 'Planned',     cls: 'badge-warning', bg: '' },
  on_hold:     { label: 'On Hold',     cls: 'badge-danger',  bg: 'bg-yellow-50' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-steel-100 text-steel-600 text-xs px-2 py-0.5 rounded-full', bg: '' },
}

// Human-readable <-> stored-key maps for the Status dropdown.
const STATUS_LABELS = ['Planned', 'In Progress', 'Completed', 'On Hold', 'Cancelled']
const LABEL_TO_KEY: Record<string, string> = { 'Planned': 'planned', 'In Progress': 'in_progress', 'Completed': 'completed', 'On Hold': 'on_hold', 'Cancelled': 'cancelled' }
const KEY_TO_LABEL: Record<string, string> = { planned: 'Planned', in_progress: 'In Progress', completed: 'Completed', on_hold: 'On Hold', cancelled: 'Cancelled' }

const ORDER_FIELDS: FormField[] = [
  { name: 'orderNo',    label: 'Order No', required: true, placeholder: 'e.g. PRO-2026-006' },
  { name: 'product',    label: 'Product', required: true, placeholder: 'e.g. Epoxy TMT 12mm' },
  { name: 'planned',    label: 'Planned (T)', type: 'number', placeholder: '0' },
  { name: 'actual',     label: 'Actual (T)', type: 'number', placeholder: '0' },
  { name: 'status',     label: 'Status', type: 'select', options: STATUS_LABELS },
  { name: 'supervisor', label: 'Supervisor', placeholder: 'e.g. Ramesh K.' },
  { name: 'start',      label: 'Start Date', type: 'date' },
  { name: 'end',        label: 'End Date', type: 'date' },
]

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

const Production: React.FC = () => {
  const { items: orders, add, update, remove } = useProductionStore()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ProdOrder | null>(null)

  const totalPlanned = orders.reduce((s, o) => s + o.planned, 0)
  const totalActual  = orders.reduce((s, o) => s + o.actual,  0)
  const efficiency   = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 100) : 0

  const handleSave = (v: Record<string, string>) => {
    const orderNo = v.orderNo.trim()
    if (orders.some(o => o.orderNo.toLowerCase() === orderNo.toLowerCase() && o.id !== editing?.id)) {
      toast.error(`Order "${orderNo}" already exists`)
      return
    }
    const record: ProdOrder = {
      id: editing ? editing.id : `pro-${Date.now()}`,
      orderNo,
      product: v.product.trim(),
      planned: num(v.planned), actual: num(v.actual),
      status: LABEL_TO_KEY[v.status] || 'planned',
      start: v.start, end: v.end,
      supervisor: v.supervisor.trim(),
    }
    if (editing) { update(editing.id, record); toast.success(`Updated ${record.orderNo}`) }
    else { add(record); toast.success(`Created ${record.orderNo}`) }
    setModalOpen(false); setEditing(null)
  }

  const handleDelete = (o: ProdOrder) => {
    if (window.confirm(`Delete order ${o.orderNo}?`)) { remove(o.id); toast.success(`Deleted ${o.orderNo}`) }
  }

  const toRecord = (o: ProdOrder): Record<string, string> => ({
    orderNo: o.orderNo, product: o.product, planned: String(o.planned), actual: String(o.actual),
    status: KEY_TO_LABEL[o.status] || 'Planned', supervisor: o.supervisor, start: o.start, end: o.end,
  })

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Production Management</h1>
          <p className="page-subtitle">Track manufacturing orders, output and shift performance</p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> New Production Order</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500">
          <p className="text-xs text-steel-500">Total Orders</p>
          <p className="text-2xl font-bold">{orders.length}</p>
        </div>
        <div className="card py-3 border-l-4 border-l-yellow-500">
          <p className="text-xs text-steel-500">In Progress</p>
          <p className="text-2xl font-bold text-blue-600">{orders.filter(o => o.status === 'in_progress').length}</p>
        </div>
        <div className="card py-3 border-l-4 border-l-green-500">
          <p className="text-xs text-steel-500">Planned Output (T)</p>
          <p className="text-2xl font-bold">{totalPlanned}</p>
        </div>
        <div className="card py-3 border-l-4 border-l-primary-500">
          <p className="text-xs text-steel-500">Actual Output (T)</p>
          <p className="text-2xl font-bold">{totalActual}</p>
          <p className="text-xs text-steel-400">{efficiency}% efficiency</p>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-steel-50 border-b border-steel-200">
                <th className="table-header">Order No</th>
                <th className="table-header">Product</th>
                <th className="table-header text-center">Planned (T)</th>
                <th className="table-header text-center">Actual (T)</th>
                <th className="table-header text-center">Efficiency</th>
                <th className="table-header">Status</th>
                <th className="table-header">Start → End</th>
                <th className="table-header">Supervisor</th>
                <th className="table-header text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-steel-400 text-sm py-10">
                    No production orders yet. Click <span className="font-semibold text-steel-600">“New Production Order”</span> to create one.
                  </td>
                </tr>
              ) : orders.map(o => {
                const eff = o.planned > 0 ? Math.round((o.actual / o.planned) * 100) : 0
                const s = STATUS_MAP[o.status] || STATUS_MAP.planned
                return (
                  <tr key={o.id} className={clsx('table-row-hover', s.bg)}>
                    <td className="table-cell font-mono text-xs font-medium text-primary-700">{o.orderNo}</td>
                    <td className="table-cell font-medium text-steel-800">{o.product}</td>
                    <td className="table-cell text-center font-bold">{o.planned}</td>
                    <td className="table-cell text-center font-bold text-green-700">{o.actual || '—'}</td>
                    <td className="table-cell text-center">
                      {o.actual > 0 ? (
                        <div>
                          <div className="w-full bg-steel-100 rounded-full h-1.5 mb-1">
                            <div className={clsx('h-1.5 rounded-full', eff >= 90 ? 'bg-green-500' : eff >= 60 ? 'bg-yellow-500' : 'bg-red-500')}
                              style={{ width: `${eff}%` }} />
                          </div>
                          <span className="text-xs font-medium">{eff}%</span>
                        </div>
                      ) : <span className="text-steel-300 text-xs">—</span>}
                    </td>
                    <td className="table-cell"><span className={s.cls}>{s.label}</span></td>
                    <td className="table-cell text-xs text-steel-500">{o.start || '—'} → {o.end || '—'}</td>
                    <td className="table-cell text-sm">{o.supervisor || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setEditing(o); setModalOpen(true) }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Update</button>
                        <button onClick={() => handleDelete(o)}
                          className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <FormModal
          title={editing ? 'Update Production Order' : 'New Production Order'}
          submitLabel={editing ? 'Save Changes' : 'Create Order'}
          fields={ORDER_FIELDS}
          initial={editing ? toRecord(editing) : {}}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default Production
